import { FC } from "react";
import cx from "classnames";
import { CardNotification, Text } from "@foris/avocado-suite";
import { UPDATED_CATALOG_FILE_NAME, type CatalogUpdateReport } from "@utils/i18nCatalogUpdate";
import StatList from "../stat-list";
import styles from "../../i18nCatalog.module.scss";

interface KeyListCardProps {
  title: string;
  keys: string[];
}

const KeyListCard: FC<KeyListCardProps> = ({ title, keys }) => {
  if (!keys.length) return null;

  return (
    <CardNotification state="info" title={`${keys.length} ${title}`} outlined>
      <ul className={styles.i18nCatalog_itemList}>
        {keys.map(key => (
          <li className={cx(styles.i18nCatalog_item, styles.i18nCatalog_item__info)} key={key}>
            <Text type="sm">{key}</Text>
          </li>
        ))}
      </ul>
    </CardNotification>
  );
};

const UpdateReport: FC<{ report: CatalogUpdateReport }> = ({ report }) => {
  const authoredResolved = report.authoredResolvedClean + report.authoredResolvedNeedsReview;

  return (
    <>
      <StatList
        items={[
          { label: "keys nuevas", value: report.newKeys.length },
          { label: "keys obsoletas", value: report.obsoleteKeys.length },
          { label: "términos de glosario agregados", value: report.glossaryEntriesAdded },
          { label: "términos de glosario actualizados", value: report.glossaryEntriesRefreshed },
          { label: "entidades del glosario cambian", value: report.changedEntities.length },
          { label: "filas que cubre el glosario", value: report.coveredByGlossary },
          { label: "propuestas listas", value: report.draftsClean },
          { label: "propuestas a revisar", value: report.draftsNeedingReview },
          { label: "textos del cliente resueltos", value: authoredResolved },
          { label: "filas a redactar a mano", value: report.manualRows.length },
          { label: "plantillas corregidas", value: report.repairedTemplates },
          { label: "plantillas mal formadas", value: report.malformedTemplates.length },
        ]}
      />

      <div className={styles.i18nCatalog_notifications}>
        <CardNotification state="success" title="Catálogo actualizado" outlined>
          <Text type="sm">
            Se descargó <strong>{UPDATED_CATALOG_FILE_NAME}</strong>. Antes de enviárselo al
            cliente, revisa en la columna Notas las filas marcadas para revisión.
          </Text>
        </CardNotification>

        {!!report.coveredByGlossary && (
          <CardNotification
            state="info"
            title={`${report.coveredByGlossary} filas las cubre el glosario`}
            outlined
          >
            <Text type="sm">
              Su texto es únicamente el término, así que cambiarlo en la hoja Glosario ya alcanza a
              todas de una vez y no hace falta proponer nada fila por fila. Quedaron marcadas como
              CUBIERTA POR EL GLOSARIO en la columna Notas.
            </Text>
          </CardNotification>
        )}

        {!!authoredResolved && (
          <CardNotification state="info" title="Se resolvieron referencias del cliente" outlined>
            <Text type="sm">
              En {authoredResolved} filas el cliente escribió su propio texto dejando adentro
              referencias {"{{glossary:...}}"}. Se reemplazaron por el término del glosario sin
              tocar su redacción, tomando la mayúscula o minúscula de cómo aparece hoy en la app.{" "}
              {report.authoredResolvedNeedsReview} quedaron marcadas para revisar en la columna
              Notas.
            </Text>
          </CardNotification>
        )}

        {!!report.repairedTemplates && (
          <CardNotification
            state="info"
            title={`${report.repairedTemplates} plantillas corregidas`}
            outlined
          >
            <Text type="sm">
              Traían una referencia dentro de otra, del tipo {"{{glossary:{{glossary:section}}}}"},
              que deja un buscar y reemplazar aplicado dos veces. Se desanidaron a{" "}
              {"{{glossary:section}}"} en la columna Plantilla y quedaron marcadas en la columna
              Notas. Solo se tocó la plantilla: ningún texto escrito por el cliente cambió.
            </Text>
          </CardNotification>
        )}

        {!!report.malformedTemplates.length && (
          <CardNotification
            state="warning"
            title={`${report.malformedTemplates.length} plantillas mal formadas`}
            outlined
          >
            <Text type="sm">
              Tienen una referencia sin cerrar o con un nombre que el glosario no admite, y no se
              puede deducir qué entidad querían nombrar. Hoy no rompen nada porque esas entidades no
              cambian, pero el día que cambien expanden mal. Están marcadas en la columna Notas del
              archivo.
            </Text>
          </CardNotification>
        )}

        {!!report.glossaryWarnings.length && (
          <CardNotification state="warning" title="Problemas en la hoja Glosario" outlined>
            <ul className={styles.i18nCatalog_list}>
              {report.glossaryWarnings.map(warning => (
                <li key={warning}>
                  <Text type="sm">{warning}</Text>
                </li>
              ))}
            </ul>
          </CardNotification>
        )}

        {(!!report.draftsClean || !!report.draftsNeedingReview) && (
          <CardNotification state="info" title="Se pre-rellenaron propuestas del glosario" outlined>
            <Text type="sm">
              Las filas donde el término va dentro de una frase traen el texto propuesto en
              &quot;Nuevo texto&quot;, marcado en la columna Notas. Las {report.draftsNeedingReview}{" "}
              marcadas como REVISAR CONCORDANCIA insertan un término de otro género o de varias
              palabras, así que hay que ajustar artículos y adjetivos alrededor antes de darlas por
              buenas.
            </Text>
          </CardNotification>
        )}

        {!!report.manualRows.length && (
          <CardNotification
            state="warning"
            title={`${report.manualRows.length} filas no se pudieron expandir`}
            outlined
          >
            <ul className={styles.i18nCatalog_itemList}>
              {report.manualRows.map(manual => (
                <li
                  className={styles.i18nCatalog_item}
                  key={`${manual.locale}-${manual.sheetName}-${manual.rowNumber}-${manual.fullKey}`}
                >
                  <Text type="sm">
                    [{manual.locale.toUpperCase()}] {manual.fullKey}
                  </Text>
                  <Text className={styles.i18nCatalog_itemLocation} type="xs">
                    {manual.reason} — pestaña &quot;{manual.sheetName}&quot;, fila{" "}
                    {manual.rowNumber}
                  </Text>
                </li>
              ))}
            </ul>
          </CardNotification>
        )}

        <KeyListCard title="keys nuevas agregadas al catálogo" keys={report.newKeys} />
        <KeyListCard title="keys que ya no existen en la app" keys={report.obsoleteKeys} />
      </div>
    </>
  );
};

export default UpdateReport;
